import { formatDateTime } from "@/lib/format";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import type { LogEntry } from "./types";
import { statusVariant } from "./flow";

/** The step log as a table: when, which step, what happened (or would have, in shadow mode). */
export function LogTable({ entries, showAutomation }: { entries: LogEntry[]; showAutomation?: boolean }) {
  return (
    <Card className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="border-b bg-[var(--color-muted)] text-left text-xs text-[var(--color-muted-foreground)]">
          <tr>
            <th className="px-3 py-2">When</th>
            {showAutomation && <th className="px-3 py-2">Automation</th>}
            <th className="px-3 py-2">Step</th>
            <th className="px-3 py-2">Result</th>
            <th className="px-3 py-2">Details</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((l, i) => (
            <tr key={i} className="border-b align-top last:border-0">
              <td className="whitespace-nowrap px-3 py-1.5 text-xs">{formatDateTime(l.occurred_at)}</td>
              {showAutomation && <td className="px-3 py-1.5 text-xs">{l.automation_name}</td>}
              <td className="px-3 py-1.5">{l.step_label || l.step_key}</td>
              <td className="px-3 py-1.5">
                <Badge variant={statusVariant(l.status)}>{l.status === "shadow" ? "would do" : l.status}</Badge>
              </td>
              <td className="px-3 py-1.5 text-xs text-[var(--color-muted-foreground)]">{describe(l)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

/** One plain line for a step-log entry's details. */
export function describe(l: LogEntry): string {
  const d = l.detail ?? {};
  const s = (k: string) => (d[k] === undefined || d[k] === null ? "" : String(d[k]));
  switch (l.step_type) {
    case "email":
      return [s("subject") && `“${s("subject")}”`, s("reason")].filter(Boolean).join(" — ");
    case "wait":
    case "wait_until":
    case "time_window":
      return d.until ? `until ${formatDateTime(String(d.until))}` : d.waited_until ? "wait over" : "";
    case "branch":
      return `took: ${s("matched")}`;
    case "enter":
      return `started (${s("source") || "by hand"})${d.shadow ? ", shadow" : ""}`;
    case "exit":
      return `${s("status").replace("_", " ")}: ${s("reason")}`;
    case "webhook":
      return [s("url"), s("http_status") && `HTTP ${s("http_status")}`, s("error")].filter(Boolean).join(" · ");
    case "staff_alert":
      return [s("subject"), s("reason")].filter(Boolean).join(" — ");
    case "sign_in_link": {
      const what = `${s("purpose") || "welcome"} link for ${s("site")}`;
      if (l.status === "shadow") return `would email a ${what}`;
      if (d.error) return `${what}: ${s("error")}`;
      if (d.reason) return `${what}: ${s("reason")}`;
      if (d.link_id) return `emailed a ${what}${d.expires_at ? `, works until ${formatDateTime(String(d.expires_at))}` : ""}`;
      return `${what} queued`;
    }
    case "facebook_audience": {
      const list = (k: string) => ((d[k] as string[] | undefined) ?? []).join(", ");
      const what = [list("add") && `add to ${list("add")}`, list("remove") && `remove from ${list("remove")}`].filter(Boolean).join("; ");
      if (l.status === "shadow") return `would ${what}`;
      const why: Record<string, string> = {
        switched_off: "Facebook moves are switched off",
        no_audience_id: "audience has no Meta id (not synced)",
        not_configured: "Zapier hooks not set",
        no_email: "no email address",
        config_error: s("message") || "unknown audience",
        already_queued: "already queued",
        not_connected: "not connected",
      };
      if (l.status === "skipped") return `${what}: ${why[s("reason")] ?? s("reason")}`;
      return `${what}: ${s("queued")} queued for Facebook`;
    }
    case "tag_add":
    case "tag_remove": {
      const ids = (d.tag_ids as number[] | undefined) ?? [];
      const verb = l.step_type === "tag_add" ? "add" : "remove";
      return `${l.status === "shadow" ? "would " + verb : verb === "add" ? "added" : "removed"} tags ${ids.join(", ")}`;
    }
    case "field_set":
      return `${s("field")} = ${d.value === null || d.value === undefined ? "(cleared)" : s("value")}`;
    case "note":
      return s("title") || (d.note_id ? `note #${s("note_id")}` : "");
  }
  return Object.keys(d).length ? JSON.stringify(d) : "";
}

